import React from 'react';
import { ArrowRight, ExternalLink, ChevronRight } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function LessonsOfAgility() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const en = {
    back: 'Writing',
    badge: 'Prologue + five articles',
    title: 'The Lessons of Agility',
    intro: `In 2001, seventeen software developers gathered at a ski lodge in Utah and wrote a document that was not a methodology. It was four value statements and twelve principles - a philosophical position about how good software gets built when the people building it are trusted to think.

Within two decades, what they wrote had become a certification industry, a consulting practice, a framework ecosystem, and a source of significant organisational confusion. Organisations declared themselves agile without changing how they made decisions. Teams adopted the vocabulary without engaging with the values. The word agile began to mean almost anything - and therefore almost nothing.

This is the gap this series is interested in.

The original insight was not primarily about software. It was about what happens when organisations stop pretending that work in complex, uncertain environments can be planned, predicted and controlled in the ways they were once designed to plan, predict and control it. That insight remains as relevant as it was in 2001 - more relevant, arguably, given how much more complex and uncertain most organisations' environments have become since then.

The framework layer can be useful. But it is not the point. This series strips it back and asks what the philosophy actually says - and what it genuinely demands of the people who lead organisations trying to work differently.`,
    articles: [{
      n: 'P',
      title: 'Why I Write About This',
      desc: 'A personal account of what drew me to agility - and why the gap between the philosophy and the practice is worth writing about.',
      live: false
    }, {
      n: '01',
      title: 'The Philosophy, Not the Framework',
      desc: 'The Agile Manifesto was not a methodology. It was four value statements. Most agile transformations never engage with the actual values.',
      live: false
    }, {
      n: '02',
      title: 'Teams Are Not Groups',
      desc: 'The research on team effectiveness is clear and mostly ignored. What it takes for a group of people to become a high-performing team is not complicated - but it is demanding.',
      live: false
    }, {
      n: '03',
      title: 'Complexity Is Not Difficulty',
      desc: 'The Cynefin distinction between complicated and complex matters enormously for how you lead. Most organisations manage complex problems as if they were complicated.',
      live: false
    }, {
      n: '04',
      title: 'Leadership in an Agile Environment',
      desc: 'What changes - and what does not - when an organisation tries to operate more agilely. The leadership behaviours that help, and the ones that undermine the whole effort.',
      live: false
    }, {
      n: '05',
      title: 'What This Actually Means Here',
      desc: 'From philosophy to practice: what agility looks like in organisations that are not software companies, and what gets in the way.',
      live: false
    }],
    substackCta: 'Follow on Substack - series publishing soon',
    relatedTitle: 'Related',
    related: [{
      label: 'Organisational Agility Workshop',
      href: '/services/workshops'
    }, {
      label: 'First-Time Manager',
      href: '/services/courses/first-time-manager'
    }]
  };
  const is = {
    back: 'Skrif',
    badge: 'Formáli + fimm greinar',
    title: 'Lærdómar frá Agile',
    intro: `Agile: Frá hugsjón til iðnaðar

Árið 2001 komu sautján hugbúnaðarsérfræðingar saman á skíðahóteli í Utah og skrifuðu skjal sem var ekki vinnulagsaðferð. Þetta voru fjórar gildisyfirlýsingar og tólf meginreglur – heimspekileg afstaða til þess hvernig góður hugbúnaður verður til þegar fólkinu sem smíðar hann er treyst til að hugsa.

Innan tveggja áratuga var það sem þeir skrifuðu orðið að vottunariðnaði, ráðgjafastarfsemi, vistkerfi rammaverka og uppsprettu verulegs ruglings innan skipulagsheilda. Fyrirtæki lýstu því yfir að þau væru orðin „Agile" án þess að breyta því hvernig ákvarðanir voru teknar. Teymi tóku upp orðafærið án þess að tileinka sér gildin. Orðið Agile fór að merkja nánast hvað sem er – og þar af leiðandi nánast ekki neitt.

Það er þetta bil sem þessi sería hefur áhuga á.

Upprunalega innsýnin snerist ekki fyrst og fremst um hugbúnað. Hún snerist um það sem gerist þegar skipulagsheildir hætta að láta eins og hægt sé að skipuleggja, spá fyrir um og stjórna vinnu í flóknu og óvissu umhverfi á sama hátt og þau voru upphaflega hönnuð til að gera. Sú innsýn á jafn mikið erindi í dag og hún átti árið 2001 – og raunar enn meira, þegar litið er til þess hve miklu flóknara og óvissara rekstrarumhverfi flestra hefur orðið síðan þá.

Umgjörðin í kringum þetta getur verið gagnleg. En hún er ekki aðalatriðið. Þessi sería flettir henni ofan af og spyr hvað heimspekin segir í raun og veru – og hvers hún krefst í raun af fólkinu sem leiðir skipulagsheildir sem reyna að vinna á annan hátt.`,
    articles: [{
      n: 'F',
      title: 'Hvers vegna ég skrifa um þetta',
      desc: 'Persónuleg frásögn af því sem dróg mig að Agile - og hvers vegna bilurinn milli heimspekinnar og framkvæmdarinnar er þess virði að skrifa um.',
      live: false
    }, {
      n: '01',
      title: 'Heimspekinn, ekki ramminn',
      desc: 'Agile-yfirlýsingin var ekki aðferðafræði. Hún var fjórar gildisvissur. Flestar Agile-umbreytingar taka aldrei á raunverulegum gildunum.',
      live: false
    }, {
      n: '02',
      title: 'Teymi eru ekki hópar',
      desc: 'Rannsóknir á teymisárangur eru skýrar og að mestu hunsaðar. Hvað það krefst að hópur fólks verði hágæða teymi er ekki flókið - en það er krefjandi.',
      live: false
    }, {
      n: '03',
      title: 'Flækjustig er ekki erfiðleiki',
      desc: 'Cynefin-munurinn milli flókinna og flæktra vandamála skiptir geysilega máli fyrir hvernig þú leiðir. Flestar stofnanir stjórna flæktu vandamáli eins og það væri flókið.',
      live: false
    }, {
      n: '04',
      title: 'Leiðtogi í Agile-umhverfi',
      desc: 'Hvað breytist - og hvað gerist ekki - þegar stofnun reynir að starfa á Agile-hátt. Leiðtogahegðunin sem hjálpar, og sú sem grafar undan allri áheitunni.',
      live: false
    }, {
      n: '05',
      title: 'Hvað þetta þýðir í raun',
      desc: 'Frá heimspeki til framkvæmdar: hvernig Agile lítur út í stofnunum sem eru ekki hugbúðarfyrirtæki, og hvað er í veginum.',
      live: false
    }],
    substackCta: 'Fylgjast á Substack - röðin kemur fljótlega',
    relatedTitle: 'Tengt',
    related: [{
      label: 'Agile workshop',
      href: '/services/workshops'
    }, {
      label: 'Stjórnandi í fyrsta sinn',
      href: '/services/courses/first-time-manager'
    }]
  };
  const c = isIS ? is : en;
  return <PageTransition data-id="element-2098">
      <div className="pt-12 pb-24" data-id="element-2099">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2100">
          <Animate data-id="element-2101">
            <Link to="/writing" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal mb-8 transition-colors" data-id="element-2102">
              <ChevronRight size={14} className="rotate-180" data-id="element-2103" />
              {c.back}
            </Link>
          </Animate>
          <Animate data-id="element-2104">
            <span className="inline-block px-3 py-1 bg-teal/10 text-teal font-medium rounded-full text-xs mb-4" data-id="element-2105">
              {c.badge}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6" data-id="element-2106">
              {c.title}
            </h1>
            <div className="prose prose-lg text-gray-500 leading-relaxed mb-12 space-y-4" data-id="element-2107">
              {c.intro.split('\n\n').map((para, i) => <p key={i} data-id="element-2108">{para}</p>)}
            </div>
          </Animate>
          <div className="space-y-4 mb-12" data-id="element-2109">
            {c.articles.map((a, i) => <Animate key={a.n} delay={i * 0.05} data-id="element-2110">
                <Card className="p-6 border border-gray-100 opacity-70" data-id="element-2111">
                  <div className="flex items-start gap-4" data-id="element-2112">
                    <span className="text-2xl font-bold text-gray-200 font-mono flex-shrink-0 leading-none mt-1" data-id="element-2113">
                      {a.n}
                    </span>
                    <div className="flex-1" data-id="element-2114">
                      <div className="flex items-center gap-2 mb-1" data-id="element-2115">
                        <h2 className="font-bold text-charcoal" data-id="element-2116">{a.title}</h2>
                        <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-medium" data-id="element-2117">
                          Coming soon
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2118">
                        {a.desc}
                      </p>
                    </div>
                  </div>
                </Card>
              </Animate>)}
          </div>
          <Animate data-id="element-2119">
            <Card className="p-6 bg-teal/5 border-teal/20 border text-center mb-12" data-id="element-2120">
              <p className="text-gray-600 mb-4" data-id="element-2121">{c.substackCta}</p>
              <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" data-id="element-2122">
                <Button variant="outline" rightIcon={<ExternalLink size={16} data-id="element-2124" />} data-id="element-2123">
                  Substack
                </Button>
              </a>
            </Card>
          </Animate>
          <Animate data-id="element-2125">
            <div data-id="element-2126">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2127">
                {c.relatedTitle}
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-2128">
                {c.related.map(r => <Link key={r.href} to={r.href} data-id="element-2129">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRight size={14} data-id="element-2131" />} data-id="element-2130">
                      {r.label}
                    </Button>
                  </Link>)}
              </div>
            </div>
          </Animate>
        </div>
      </div>
    </PageTransition>;
}